import cloneDeep from 'lodash/cloneDeep'

export const Page = (router) => {
  const pageValue = router.query.page
  if (pageValue == null || typeof pageValue === 'undefined') {
    return 1
  }
  return Number(pageValue)
}

export const Keyword = (router) => {
  return router.query.q
}

export const Location = (router) => {
  const locations = router.query.locations

  if (!locations) {
    return null
  }

  const lowerCaseLocations = locations.toLowerCase()

  if (lowerCaseLocations === 'everywhere') {
    return null
  }

  if (lowerCaseLocations === 'canada' || lowerCaseLocations === 'usa') {
    return {
      country: lowerCaseLocations,
    }
  }

  const lowerCaseLocationValues = lowerCaseLocations && lowerCaseLocations.split(',')
  if (lowerCaseLocationValues.length === 1) {
    return {
      state: lowerCaseLocationValues[0],
    }
  }

  if (lowerCaseLocationValues.length === 2) {
    return {
      state: lowerCaseLocationValues[1],
      city: lowerCaseLocationValues[0],
    }
  }

  return {
    state: lowerCaseLocationValues[2],
    city: lowerCaseLocationValues[1],
    neighborhood: lowerCaseLocationValues[0],
  }
}

export const Facets = (router) => {
  const facets = router.query.facets
  if (!facets) {
    return null
  }
  return facets.toLowerCase().split(',').sort()
}

export const DirectoryLinkHref = ({
  directoryProviders,
  directoryPath,
  location,
  facetSlugs,
  page,
  keyword,
}) => {
  const directoryLinkPath = DirectoryLinkPath({
    directoryProviders,
    directoryPath,
    location,
    facetSlugs,
    keyword,
  })
  let query = {}

  const facetsQueryValue = getFacetsPath(facetSlugs, directoryProviders)
  const locationsQueryValue = getLocationPath(location)
  if (keyword) {
    query = {
      ...query,
      q: keyword,
    }
  }
  if (facetsQueryValue) {
    query = {
      ...query,
      facets: facetsQueryValue,
    }

    if (locationsQueryValue) {
      query = {
        ...query,
        locations: locationsQueryValue,
      }
    }
  } else if (hasLocations(location) && locationsQueryValue) {
    query = {
      ...query,
      locations: locationsQueryValue,
    }
  }

  if (page != null && page > 1) {
    query = {
      ...query,
      page: page,
    }
  }
  if (query !== {}) {
    return { pathname: directoryLinkPath, query: query }
  }

  return { pathname: directoryPath }
}

const getLocationPath = (location) => {
  if (!location || location.country === null) {
    return 'everywhere'
  }

  if (location.city && location.state && location.neighborhood) {
    return `${location.neighborhood},${location.city},${location.state}`
  }

  if (location.city && location.state) {
    return `${location.city},${location.state}`
  }

  if (location.state) {
    return `${location.state}`
  }

  return `${location.country}`
}

function hasFacets(facetSlugs, directoryProviders) {
  const facets = getFacetsForUrl(facetSlugs, directoryProviders)
  return facets && facets.length > 0
}

function hasLocations(location) {
  return location && !(location.type === LOCATION_TYPES.COUNTRIES && location.country === null)
}

export const DirectoryLinkPath = ({
  directoryProviders,
  directoryPath,
  location,
  facetSlugs,
  keyword,
}) => {
  if (hasFacets(facetSlugs, directoryProviders)) {
    return `${directoryPath}/[locations]/[facets]`
  }

  if (hasLocations(location)) {
    return `${directoryPath}/[locations]`
  }

  return `${directoryPath}`
}

const getProviderFacetSlugs = (providers) => {
  const results = []
  if (!providers || providers.length === 0) {
    return results
  }

  if (providersContain(providers, PROVIDERS.BABYSITTERS)) {
    if (providers.length <= 2) {
      return results
    }
    return providersRemove(providers, PROVIDERS.BABYSITTERS).map(
      (provider) => provider.facetKeyword
    )
  }

  if (providers.length === 1) {
    return results
  }
  return providers.map((provider) => provider.facetKeyword)
}

function getFacetsForUrl(facetSlugs, directoryProviders) {
  const hasFacetSlugs = facetSlugs && facetSlugs.length > 0
  const hasDirectoryProviders = directoryProviders && directoryProviders.length > 0
  if (hasFacetSlugs) {
    if (hasDirectoryProviders) {
      const providerFacetSlugs = getProviderFacetSlugs(directoryProviders)
      if (providerFacetSlugs && providerFacetSlugs.length > 0) {
        return [...facetSlugs, ...providerFacetSlugs]
      }
    }
    return facetSlugs
  }

  if (hasDirectoryProviders) {
    return getProviderFacetSlugs(directoryProviders)
  }

  return []
}

function getFacetsPath(facetSlugs, directoryProviders) {
  const facets = getFacetsForUrl(facetSlugs, directoryProviders)
  if (!facets || facets.length === 0) {
    return null
  }

  return facets.sort().join(',')
}

const directoryLinkAsPathname = ({ directoryProviders, directoryPath, location, facetSlugs }) => {
  if (hasFacets(facetSlugs, directoryProviders)) {
    const locationPath = getLocationPath(location)
    const facetsPath = getFacetsPath(facetSlugs, directoryProviders)
    if (facetsPath) {
      return `${directoryPath}/${locationPath}/${facetsPath}`
    }
  }

  if (location && !(location.type === LOCATION_TYPES.COUNTRIES && location.country === null)) {
    const locationPath = getLocationPath(location)
    return `${directoryPath}/${locationPath}`
  }

  return `${directoryPath}`
}

export const DirectoryLinkAs = ({
  directoryProviders,
  directoryPath,
  location,
  facetSlugs,
  page,
  keyword,
}) => {
  const pathname = directoryLinkAsPathname({
    directoryProviders,
    directoryPath,
    location,
    facetSlugs,
  })
  const hasPage = page && page > 1
  if (hasPage && keyword) {
    return `${pathname}?page=${page}&q=${keyword}`
  } else if (hasPage) {
    return `${pathname}?page=${page}`
  } else if (keyword) {
    return `${pathname}?q=${keyword}`
  }

  return pathname
}

const getDefaultSiteDirectoryResultsType = (siteDirectoryResultsTypes, directoryResultsType) => {
  for (const siteDirectoryResultsType of siteDirectoryResultsTypes) {
    if (siteDirectoryResultsType.type === directoryResultsType) {
      return siteDirectoryResultsType
    }
  }
  return null
}

export const WithResultType = (
  directoryLinkInformation,
  siteDirectoryResultsTypes,
  directoryResultsType
) => {
  if (!directoryLinkInformation) {
    return null
  }

  const newDirectoryLinkInformation = cloneDeep(directoryLinkInformation)
  newDirectoryLinkInformation.page = 1
  newDirectoryLinkInformation.directoryPath = getDirectoryPath(
    newDirectoryLinkInformation.directoryProviders,
    directoryResultsType,
    siteDirectoryResultsTypes
  )
  newDirectoryLinkInformation.directoryResultsType = directoryResultsType

  return newDirectoryLinkInformation
}

export const Reset = (directoryLinkInformation, siteDirectoryResultsTypes) => {
  if (!directoryLinkInformation) {
    return null
  }

  const defaultSiteDirectoryResultsType = getDefaultSiteDirectoryResultsType(
    siteDirectoryResultsTypes,
    directoryLinkInformation.directoryResultsType
  )
  if (
    defaultSiteDirectoryResultsType.href === '/sitters' ||
    defaultSiteDirectoryResultsType.href === '/sitter-jobs'
  ) {
    if (directoryLinkInformation.directoryResultsType === RESULTS_TYPES.PROFILES) {
      return {
        page: 1,
        directoryPath: PROVIDERS.BABYSITTERS.profilesBasePath,
        directoryResultsType: directoryLinkInformation.directoryResultsType,
      }
    }
    return {
      page: 1,
      directoryPath: PROVIDERS.BABYSITTERS.jobsBasePath,
      directoryResultsType: directoryLinkInformation.directoryResultsType,
    }
  }
  return {
    page: 1,
    directoryPath: defaultSiteDirectoryResultsType.href,
    directoryResultsType: directoryLinkInformation.directoryResultsType,
  }
}

export const Provider = (slug, keyword = null) => {
  if (!slug) {
    return null
  }

  for (const provider of Object.values(PROVIDERS)) {
    if (
      provider.facetSlug === slug ||
      provider.facetKeyword === slug ||
      (provider.facetKeyword && provider.facetKeyword === keyword)
    ) {
      return provider
    }
  }

  return null
}

export const Providers = (facetSlugs) => {
  const providers = []
  if (!facetSlugs) {
    return providers
  }

  for (const facetSlug of facetSlugs) {
    const provider = Provider(facetSlug)
    if (provider) {
      providers.push(provider)
    }
  }

  return providers
}

export const NonProviderSlugs = (facetSlugs) => {
  const nonProviderSlugs = []
  if (!facetSlugs) {
    return nonProviderSlugs
  }

  for (const facetSlug of facetSlugs) {
    if (!isProvider(facetSlug)) {
      nonProviderSlugs.push(facetSlug)
    }
  }

  return nonProviderSlugs
}

const providersContain = (providers, provider) => {
  if (!provider || !providers || providers.length === 0) {
    return false
  }
  for (const givenProvider of providers) {
    if (providersMatch(givenProvider, provider)) {
      return true
    }
  }
  return false
}

function providersMatch(givenProvider, provider) {
  if (!givenProvider || !provider) {
    return false
  }
  return (
    givenProvider.facetSlug === provider.facetSlug ||
    (givenProvider.facetKeyword != null && givenProvider.facetKeyword === provider.facetKeyword)
  )
}

const providersRemove = (providers, provider) => {
  if (!providers || providers.length === 0) {
    return []
  }

  if (!provider) {
    return providers
  }

  const results = []
  for (const givenProvider of providers) {
    if (!providersMatch(givenProvider, provider)) {
      results.push(givenProvider)
    }
  }
  return results
}

const isProvider = (slug, keyword) => {
  const provider = Provider(slug, keyword)

  return provider != null
}

export const RemoveDuplicateProviders = (providers) => {
  const results = []
  if (!providers) {
    return results
  }

  for (const provider of providers) {
    if (!providersContain(results, provider)) {
      results.push(provider)
    }
  }

  return results
}

const removeDuplicateFacetSlugs = (facetSlugs) => {
  const results = []
  if (!facetSlugs) {
    return results
  }

  for (const facetSlug of facetSlugs) {
    if (!results.includes(facetSlug)) {
      results.push(facetSlug)
    }
  }

  return results
}

function getAllProviders(directoryProviders, provider = null) {
  const providers = directoryProviders || []
  if (provider) {
    providers.push(provider)
  }

  const nonDuplicatedProviders = RemoveDuplicateProviders(providers)
  if (
    providersMatch(PROVIDERS.INDIVIDUALS, provider) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.INDIVIDUALS) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.BUSINESS)
  ) {
    return providersRemove(nonDuplicatedProviders, PROVIDERS.BUSINESS)
  } else if (
    providersMatch(PROVIDERS.BUSINESS, provider) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.INDIVIDUALS) &&
    providersContain(nonDuplicatedProviders, PROVIDERS.BUSINESS)
  ) {
    return providersRemove(nonDuplicatedProviders, PROVIDERS.INDIVIDUALS)
  }
  return nonDuplicatedProviders
}

function toggleProvider(directoryProviders, provider) {
  if (providersContain(directoryProviders, provider)) {
    return getAllProviders(providersRemove(directoryProviders, provider))
  }
  return getAllProviders(directoryProviders, provider)
}

function getDirectoryPath(providers, directoryResultsType, siteDirectoryResultsTypes) {
  if (providers.length === 1) {
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return providers[0].profilesBasePath
    }
    return providers[0].jobsBasePath
  } else if (providers.length > 2 && providersContain(providers, PROVIDERS.BABYSITTERS)) {
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return PROVIDERS.BABYSITTERS.profilesBasePath
    }
    return PROVIDERS.BABYSITTERS.jobsBasePath
  } else if (providers.length === 2 && providersContain(providers, PROVIDERS.BABYSITTERS)) {
    const nonBabysitterProviders = providersRemove(providers, PROVIDERS.BABYSITTERS)
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return nonBabysitterProviders[0].profilesBasePath
    }
    return nonBabysitterProviders[0].jobsBasePath
  }
  const siteDirectoryResultsType = getDefaultSiteDirectoryResultsType(
    siteDirectoryResultsTypes,
    directoryResultsType
  )

  if (
    siteDirectoryResultsType.href === '/sitters' ||
    siteDirectoryResultsType.href === '/sitter-jobs'
  ) {
    if (directoryResultsType === RESULTS_TYPES.PROFILES) {
      return PROVIDERS.BABYSITTERS.profilesBasePath
    }
    return PROVIDERS.BABYSITTERS.jobsBasePath
  }
  return siteDirectoryResultsType.href
}

export const WithProvider = (directoryLinkInformation, siteDirectoryResultsTypes, provider) => {
  if (!directoryLinkInformation || !siteDirectoryResultsTypes || !provider) {
    return null
  }
  const newDirectoryLinkInformation = cloneDeep(directoryLinkInformation)

  const providers = toggleProvider(newDirectoryLinkInformation.directoryProviders, provider)
  return {
    location: newDirectoryLinkInformation.location,
    page: 1,
    directoryProviders: providers,
    directoryPath: getDirectoryPath(
      providers,
      newDirectoryLinkInformation.directoryResultsType,
      siteDirectoryResultsTypes
    ),
    directoryResultsType: newDirectoryLinkInformation.directoryResultsType,
    facetSlugs: newDirectoryLinkInformation.facetSlugs,
  }
}

const toggleFacetSlugs = (facetSlugs, facetSlug) => {
  const nonDuplicateFacetSlugs = removeDuplicateFacetSlugs(facetSlugs)
  if (nonDuplicateFacetSlugs) {
    if (nonDuplicateFacetSlugs.includes(facetSlug)) {
      const filter = nonDuplicateFacetSlugs.filter((item) => item !== facetSlug)
      return filter
    }
    nonDuplicateFacetSlugs.push(facetSlug)
    return nonDuplicateFacetSlugs
  }

  return [facetSlug]
}

export const WithLocation = (directoryLinkInformation, location) => {
  if (!directoryLinkInformation || !location) {
    return null
  }

  const newDirectoryLinkInformation = cloneDeep(directoryLinkInformation)

  return {
    ...newDirectoryLinkInformation,
    page: 1,
    location: location,
  }
}

export const WithFacetSlug = (directoryLinkInformation, facetSlug) => {
  if (!directoryLinkInformation || !facetSlug) {
    return null
  }

  const newDirectoryLinkInformation = cloneDeep(directoryLinkInformation)

  return {
    ...newDirectoryLinkInformation,
    page: 1,
    facetSlugs: toggleFacetSlugs(newDirectoryLinkInformation.facetSlugs, facetSlug),
  }
}

export const WithPage = (directoryLinkInformation, page) => {
  if (!directoryLinkInformation) {
    return null
  }

  const newDirectoryLinkInformation = cloneDeep(directoryLinkInformation)
  newDirectoryLinkInformation.page = page

  return newDirectoryLinkInformation
}

export const withSearchQuery = (directoryLinkInformation, searchValue) => {
  if (!directoryLinkInformation) {
    return null
  }

  const newDirectoryLinkInformation = cloneDeep(directoryLinkInformation)
  newDirectoryLinkInformation.keyword = searchValue

  return newDirectoryLinkInformation
}

export const RESULTS_TYPES = {
  JOBS: 'jobs',
  PROFILES: 'profiles',
}

export const PROVIDERS = {
  INDIVIDUALS: {
    facetSlug: 'individuals',
    facetKeyword: 'individuals',
    profilesBasePath: '/individuals',
    jobsBasePath: '/individuals-jobs',
  },
  DAYCARES: {
    facetSlug: 'services_daycare',
    facetKeyword: 'daycare',
    profilesBasePath: '/daycares',
    jobsBasePath: '/daycare-jobs',
  },
  NANNIES: {
    facetSlug: 'services_nanny',
    facetKeyword: 'nanny',
    profilesBasePath: '/nannies',
    jobsBasePath: '/nanny-jobs',
  },
  BUSINESS: {
    facetSlug: 'business',
    facetKeyword: 'business',
    profilesBasePath: '/businesses',
    jobsBasePath: '/business-jobs',
  },
  BABYSITTERS: {
    facetSlug: 'services_babysitter',
    facetKeyword: 'babysitter',
    profilesBasePath: '/babysitters',
    jobsBasePath: '/babysitter-jobs',
  },
}

export const LOCATION_TYPES = {
  COUNTRIES: 'countries',
  STATES: 'states',
  CITIES: 'cities',
  NEIGHBORHOODS: 'neighborhoods',
}
